import dayjs from 'dayjs';
import ShelfController from '../../core/shelf.controller';
import { User } from '../../data';
import { canAccess } from '../../core/access';
import { titleize, normCat } from './sosHeat';

const isCanceled = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

export const CHANNEL_OPTIONS = [
    { label: 'Moderno', value: 'moderno' },
    { label: 'Tradicional', value: 'tradicional' }
];

export const DEFAULT_FILTERS = { channel: [], tagId: [], storeType: [] };

class ShareOfShelfController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }
        const user = User.getInstance();
        if (!canAccess(user?.userType, 'share-of-shelf')) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }
        try {
            // Category options come from the fast Product.distinct endpoint — NOT the heavy SOS
            // aggregation — so the Table 2 filter is populated instantly. Keys are normalized to
            // match the server's byCategory keys.
            const [clients, tagsRes, catsRes, storeTypesRes] = await Promise.all([
                this.data.fetchClients().catch(() => []),
                this.data.fetchTags().catch(() => null),
                this.data.fetchProductCategories().catch(() => null),
                this.data.fetchStoreTypes().catch(() => [])
            ]);
            const clientOptions = (clients || [])
                .filter((c) => c?._id && c?.name)
                .map((c) => ({ label: c.name, value: c._id }))
                .sort((a, b) => a.label.localeCompare(b.label));
            const tagOptions = (tagsRes?.tags || [])
                .filter((t) => t?._id && (t.applicableModels || []).some((m) => ['Client', 'Store'].includes(m)))
                .map((t) => ({ label: t.name, value: t._id, color: t.color }));
            const storeTypeOptions = (storeTypesRes || [])
                .filter((t) => t?.value && t?.active !== false)
                .map((t) => ({ label: t.label || t.value, value: t.value }))
                .sort((a, b) => a.label.localeCompare(b.label));
            const seen = new Set();
            const categoryOptions = (catsRes?.categories || [])
                .map((c) => ({ label: titleize(c.name || c.value), value: normCat(c.name || c.value) }))
                .filter((o) => o.value && !seen.has(o.value) && seen.add(o.value))
                .sort((a, b) => a.label.localeCompare(b.label));
            this.setState({ clientOptions, tagOptions, categoryOptions, storeTypeOptions, loading: false });
            await this.loadOverview();
        } catch (err) {
            this.setState({ loading: false, error: true });
        }
    }

    retry() {
        this.setState({ loading: true, error: false }, () => this.initialize());
    }

    globalParams() {
        const { filters, dateRange } = this.state;
        const q = {};
        if (filters.channel?.length) q.channel = filters.channel.join(',');
        if (filters.tagId?.length) q.tagId = filters.tagId.join(',');
        if (filters.storeType?.length) q.storeType = filters.storeType.join(',');
        if (dateRange?.[0] && dateRange?.[1]) {
            // UTC calendar-day boundaries so the end day (today included) is always covered regardless
            // of browser timezone — store-check createdAt is Venezuela-local time cast to UTC.
            q.startDate = `${dateRange[0].format('YYYY-MM-DD')}T00:00:00.000Z`;
            q.endDate = `${dateRange[1].format('YYYY-MM-DD')}T23:59:59.999Z`;
        }
        return q;
    }

    // Table 1 + header stats (all categories compiled). No category needed.
    async loadOverview() {
        this._ovAbort?.abort();
        const abort = new AbortController();
        this._ovAbort = abort;
        this.setState({ dataLoading: true });
        try {
            const res = await this.data.fetchShareOfShelf(this.globalParams(), abort.signal);
            if (this._ovAbort !== abort) return;
            this.setState({ data: res?.data || null, dataLoading: false });
        } catch (err) {
            if (this._ovAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudo cargar el Share of Shelf');
            this.setState({ dataLoading: false });
        }
    }

    // Table 2 — the PDV of the selected client. Loaded only when a client is selected.
    async loadStores() {
        const { selectedClientId } = this.state;
        if (!selectedClientId) {
            this.setState({ clientStores: null });
            return;
        }
        this._stAbort?.abort();
        const abort = new AbortController();
        this._stAbort = abort;
        this.setState({ storesLoading: true });
        try {
            const res = await this.data.fetchShareOfShelfStores({ ...this.globalParams(), clientId: selectedClientId }, abort.signal);
            if (this._stAbort !== abort) return;
            this.setState({ clientStores: res?.data || null, storesLoading: false });
        } catch (err) {
            if (this._stAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudieron cargar los PDV del cliente');
            this.setState({ storesLoading: false });
        }
    }

    setFilter(key, value) {
        this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }), () => {
            this.loadOverview();
            this.loadStores();
        });
    }

    setDateRange(dateRange) {
        this.setState({ dateRange }, () => {
            this.loadOverview();
            this.loadStores();
        });
    }

    clearFilters() {
        this.setState(
            {
                filters: { ...DEFAULT_FILTERS },
                dateRange: [dayjs().subtract(150, 'day'), dayjs()],
                selectedClientId: null,
                selectedClientName: null,
                clientStores: null,
                table2Category: null
            },
            () => this.loadOverview()
        );
    }

    selectClient(clientId, clientName) {
        if (!clientId) return;
        this.setState({ selectedClientId: clientId, selectedClientName: clientName || '', clientStores: null }, () => this.loadStores());
    }

    // "Cliente" in the top filter bar: selecting loads its PDV; clearing drops the selection and its name.
    pickClient(v) {
        const { clientOptions } = this.state;
        if (v) this.selectClient(v, clientOptions.find((o) => o.value === v)?.label);
        else this.setState({ selectedClientId: null, selectedClientName: null, clientStores: null });
    }

    // "Cliente" inside the PDV card: same, but clearing keeps the last client name (original behavior).
    pickTable2Client(v) {
        const { clientOptions } = this.state;
        if (v) this.selectClient(v, clientOptions.find((o) => o.value === v)?.label);
        else this.setState({ selectedClientId: null, clientStores: null });
    }

    setTable2Category(v) {
        this.setState({ table2Category: v || null });
    }

    openStore(store) {
        this.setState({ drawerOpen: true, drawerStore: store });
    }

    closeStore() {
        this.setState({ drawerOpen: false });
    }

    // Map each store to the byMonth/total of the selected category (or the compiled all-category view).
    get storeRows() {
        const { clientStores, table2Category } = this.state;
        const stores = clientStores?.stores || [];
        if (!table2Category) return stores;
        return stores.map((s) => {
            const cat = s.byCategory?.[table2Category];
            return { ...s, byMonth: cat?.byMonth || {}, total: cat?.total ?? null };
        });
    }

    // Label for the Table 2 context line.
    table2CategoryLabel() {
        const { table2Category, categoryOptions } = this.state;
        if (!table2Category) return null;
        return titleize(categoryOptions.find((o) => o.value === table2Category)?.label || table2Category);
    }
}

export default ShareOfShelfController;
